import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SummaryCard from "../../../../components/core/blocks/SummaryCard";
import { DateField, Label } from "../../../../components/custom/programs/fields";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../../../components/core/basic/Button";
import { useFetch } from "../../../../hooks/api";
import { useAppSelector } from "../../../../redux/hooks";
import { HiDownload } from "react-icons/hi";
import { getURLPrefix } from "../../../../utils/getUrlPrefix";
import axios from "axios";
import FileSaver from "file-saver";
import { FaSpinner } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../utils/api";
import moment from "moment";
import Select from "react-select";
import { useSimpleCan } from "../../../../config/casl/casl";
import UnauthorizedWarning from "../../../../components/core/basic/UnauthorizedWarning";
import { CANNED_QUERIES, FEATURED_REPORTS, NEW_TO_BRAND_ASIN_QUERY } from "../../../../modules/amc/utils/utils";
import { css } from "../../../../../styled-system/css";
import { HStack } from "../../../../../styled-system/jsx";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { commaSearchFilterFn } from "../../../../components/table/filterFns";
import { resetTable } from "../../../../components/table/utils";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { Table } from "../../../../components/table/Table";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";

const API_URL = getURLPrefix();

// Module-level so the query only rebuilds rows when the response changes.
const selectExports = (data) => data?.data?.results;

const columnHelper = createAppColumnHelper();

const exportSearchFilterFn = commaSearchFilterFn((row) => `${row.name}`);

const handleDownload = async (id, title, fileType) => {
  const url = `${API_URL}/api/exports/${id}/download/`;
  const response = await axios({
    method: "GET",
    url: url,
    responseType: "blob",
  });

  const blobType =
    {
      xlsx: "vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
      csv: "text/csv;charset=utf-8",
    }?.[fileType] ?? "text;charset=utf-8";

  const blob = new Blob([response.data], {
    type: blobType,
  });

  FileSaver.saveAs(blob, `${title}.${fileType}`);
};

const Exports = () => {
  const { t } = useTranslation(["perf_reports", "common", "validation"]);
  const profileType = useAppSelector((state) => state.user.organization_type);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const dateFormatter = useDateFormatter("medium");
  const userCanReadExports = useSimpleCan("read", "Export");
  const userCanCreateExports = useSimpleCan("create", "Export");

  const userCanExportProduct = useSimpleCan("export", "Product");
  const userCanExportAd = useSimpleCan("export", "Ad");
  const userCanExportPricingPlan = useSimpleCan("export", "PricingPlan");
  const userCanExportProductAd = useSimpleCan("export", "ProductAd");
  const userCanExportOrg = useSimpleCan("export", "Org");

  const reportSubjectMap = useMemo(() => {
    const amcReportMap = {
      ...Object.fromEntries([...CANNED_QUERIES, ...FEATURED_REPORTS].map((q) => [q.name, "Ad"])),
      DSP_PERFORMANCE_BY_LOCATION_INTERNAL: "Ad",
      PATH_TO_PURCHASE_CAMPAIGNS_INTERNAL: "Ad",
      PLACEMENT_PERFORMANCE_INTERNAL: "Ad",
      SUBSCRIBE_AND_SAVE_PERFORMANCE_INTERNAL: "Ad",
      LTV_BY_AD_EXPOSURE_INTERNAL: "Ad",
      EXPOSURE_SEGMENT_PERFORMANCE_INTERNAL: "Ad",
      CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION_INTERNAL: "Ad",
    };

    return {
      product_report: "Product",
      daily_product_report: "Product",
      ad_report: "Ad",
      dynamic_pricing_price_report: "PricingPlan",
      competitor_product_report: "Product",
      price_link_report: "PricingPlan",
      pricing_change_report: "PricingPlan",
      pricing_data_export: "PricingPlan",
      org_report: "Org",
      vendor_retail_price_report: "PricingPlan",
      ...amcReportMap,
      // Add explicit mapping for BY_ASIN variant of New to Brand report
      [NEW_TO_BRAND_ASIN_QUERY]: "Ad",
    };
  }, []);

  const canExportBySubject = useMemo(
    () => ({
      Product: userCanExportProduct,
      Ad: userCanExportAd,
      PricingPlan: userCanExportPricingPlan,
      ProductAd: userCanExportProductAd,
      Org: userCanExportOrg,
    }),
    [userCanExportProduct, userCanExportAd, userCanExportPricingPlan, userCanExportProductAd, userCanExportOrg],
  );

  const reportOptions = useMemo(() => {
    const options = [
      {
        value: "product_report",
        label: t(($) => $.perf_reports["Products Report"]),
      },
      {
        value: "ad_report",
        label: t(($) => $.perf_reports["Media Plans Report"]),
      },
      {
        value: "dynamic_pricing_price_report",
        label: t(($) => $.perf_reports["Dynamic Price Report"]),
      },
      {
        value: "competitor_product_report",
        label: t(($) => $.perf_reports["Competitor Price Report"]),
      },
      {
        value: "daily_product_report",
        label: t(($) => $.perf_reports["Products Report by Day"]),
      },
      {
        value: "price_link_report",
        label: t(($) => $.perf_reports["Amazon - Walmart Price Link Report"]),
      },
      {
        value: "pricing_change_report",
        label: t(($) => $.perf_reports["Pricing Change Report"]),
      },
      {
        value: "pricing_data_export",
        label: t(($) => $.perf_reports["Pricing Data Export"]),
      },
      {
        value: "org_report",
        label: t(($) => $.perf_reports["Agency Report"]),
      },
    ];
    if (profileType === "vendor") {
      options.push({
        value: "vendor_retail_price_report",
        label: t(($) => $.perf_reports["Vendor Retail Price Report"]),
      });
    }
    return options.filter((option) => {
      return canExportBySubject?.[reportSubjectMap[option.value] ?? "Ad"];
    });
  }, [canExportBySubject, profileType, reportSubjectMap, t]);

  const { data: exports, isLoading } = useFetch(
    ["exports"],
    "/exports/",
    { ordering: "-created_on", limit: 5000 },
    {
      refetchInterval: (query) => {
        const results = query.state.data?.data?.results ?? [];
        const isPending = results.find((r) => r.status === 0 || r.status === 1);
        return isPending ? 5 * 1000 : 30 * 1000;
      },
      select: selectExports,
    },
  );
  const { handleSubmit, formState, control } = useForm({
    defaultValues: {
      start_date: moment().subtract(30, "days").toDate(),
      end_date: moment().toDate(),
      report: "product_report",
    },
  });
  const queryClient = useQueryClient();

  const makeReport = useMutation({
    mutationFn: async (data) => {
      const reportType = data.report;
      if (
        [
          "vendor_retail_price_report",
          "dynamic_pricing_price_report",
          "daily_product_report",
          "price_link_report",
          "competitor_product_report",
          "pricing_change_report",
          "pricing_data_export",
          "org_report",
        ].indexOf(reportType) > -1
      ) {
        return await api.post(`/api/custom_reports/`, {
          mp: reportType === "org_report" ? undefined : marketPlace.marketPlace,
          report_type: reportType,
          params: {
            start_date: moment(data.start_date).format("YYYY-MM-DD"),
            end_date: moment(data.end_date).format("YYYY-MM-DD"),
          },
        });
      }
      return await api.get(`/api/data_report/${reportType}`, {
        xls: true,
        task: true,
        empty: true,
        mp: marketPlace.marketPlace,
        start_date: moment(data.start_date).format("YYYY-MM-DD"),
        end_date: moment(data.end_date).format("YYYY-MM-DD"),
      });
    },

    onSuccess: async () => {
      return queryClient.invalidateQueries({
        queryKey: ["exports"],
      });
    },
  });

  const columns = useMemo(() => {
    const exportStatusLabels = {
      0: t(($) => $.perf_reports.exportStatus["Pending"]),
      1: t(($) => $.perf_reports.exportStatus["Running"]),
      2: t(($) => $.perf_reports.exportStatus["Ready"]),
      3: t(($) => $.perf_reports.exportStatus["Failed"]),
      4: t(($) => $.perf_reports.exportStatus["Cancelled"]),
    };
    return [
      columnHelper.accessor("name", {
        header: t(($) => $.perf_reports["Title"]),
        size: 400,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((row) => exportStatusLabels[row.status] || row.status, {
        id: "status",
        header: t(($) => $.perf_reports["Status"]),
        size: 120,
        enableResizing: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("progress", {
        header: t(($) => $.perf_reports["Progress"]),
        size: 110,
        enableResizing: false,
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.perf_reports["Created On"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => (getValue() ? dateFormatter.format(new Date(getValue())) : "-"),
      }),
      columnHelper.display({
        id: "download",
        header: t(($) => $.perf_reports["Download"]),
        size: 110,
        enableResizing: false,
        cell: ({ row }) => {
          const { id, name, status, file_type: fileType } = row.original;

          // Unknown export types default to "Ad" for permission check - they're probably AMC reports that we check Ads for by convention. Improve the logic here if we add more report types that don't fit this assumption.
          const subject = reportSubjectMap[row.original.export_type] ?? "Ad";
          const userCanExportSubjectType = canExportBySubject?.[subject];

          if (status === 1 || status === 0) return <FaSpinner className="fa-spin" />;

          if (status === 2 && userCanExportSubjectType) {
            return (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  handleDownload(id, name, fileType);
                }}
              >
                <HiDownload />
              </span>
            );
          }
          return <span>-</span>;
        },
      }),
    ];
  }, [t, dateFormatter, reportSubjectMap, canExportBySubject]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "reportExportsTable",
    initialState: { sorting: [{ id: "created_on", desc: true }] },
  });

  const table = useClientTable({
    data: exports,
    columns,
    getRowId: (row) => String(row.id),
    globalFilterFn: exportSearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  if (!userCanReadExports) {
    return <UnauthorizedWarning />;
  }
  return (
    <Container
      style={{
        background: "#fefefe",
        minHeight: "calc(100vh - 150px)",
      }}
    >
      <Row>
        <Col xs={12} className="mt-4">
          <SummaryCard title={t(($) => $.common.sidebar["Exports"])}>
            <Row>
              <Col xs={12} lg={8} xl={6}>
                <Row>
                  <Col xs={12} className="mb-4">
                    <Form.Group>
                      <Label>{t(($) => $.common.sidebar["Reports"])}</Label>
                      <div className="fs-standard">
                        <Controller
                          name="report"
                          control={control}
                          render={({ field }) => {
                            return (
                              <Select
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                name="Report"
                                id="report"
                                options={reportOptions}
                                value={reportOptions.find((option) => option.value === field.value)}
                                onChange={(option) => {
                                  field.onChange(option.value);
                                }}
                              />
                            );
                          }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <DateField
                      label={t(($) => $.perf_reports["Start Date"])}
                      placeholder={t(($) => $.perf_reports["Starting on..."])}
                      fieldName="start_date"
                      errors={formState.errors}
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t(($) => $.perf_reports["Start date is required"]),
                        },
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <DateField
                      label={t(($) => $.perf_reports["End Date"])}
                      placeholder={t(($) => $.perf_reports["Ending on..."])}
                      fieldName="end_date"
                      errors={formState.errors}
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t(($) => $.perf_reports["End date is required"]),
                        },
                      }}
                    />
                  </Col>
                </Row>
              </Col>
              {userCanCreateExports && (
                <Col xs={12} className="mt-3 d-flex justify-content-end">
                  <Button onPress={handleSubmit(makeReport.mutate)}>
                    {t(($) => $.perf_reports["Generate Export"])}
                  </Button>
                </Col>
              )}
            </Row>
            <Row>
              <Col xs={12}>
                {reportOptions.length ?
                  <>
                    <HStack padding={"small"} fontSize={"large"}>
                      <Button
                        variant={"contentOnly"}
                        aria-label={t(($) => $.common.table["Reset table filters"])}
                        onPress={() => resetTable(table)}
                        fontSize={"xxlarge"}
                      >
                        <MdRotateLeft />
                      </Button>
                      <RT8ColumnSelector table={table} />
                      <SearchInput
                        aria-label={t(($) => $.common.actions["Search"])}
                        isExpandable
                        css={{ flex: "0 1 350px" }}
                        value={table.state.globalFilter ?? ""}
                        onChange={table.setGlobalFilter}
                      />
                      <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
                    </HStack>
                    <Table table={table} />
                  </>
                : <p className={css({ textAlign: "center", paddingY: "large" })}>
                    {t(($) => $.perf_reports["You do not have permissions to export any reports."])}
                  </p>
                }
              </Col>
            </Row>
          </SummaryCard>
        </Col>
      </Row>
    </Container>
  );
};

export const Route = createFileRoute("/_authenticated/user/report/export")({
  component: Exports,
  staticData: { application: "Reports" },
});
